import React, { useState, useEffect, useMemo } from "react";
import DialogContent from "@mui/material/DialogContent";
import {
  IFlight,
  IGroundEvent,
  IFlightType,
  ICustomer,
  IFlightFlag,
  IFlightAircraft,
  IBulkOpsState,
  BulkActionType,
  IBulkEventPayload,
  minuteInMilliseconds,
  IFlightFlagSubDoc,
  ClientSettingName,
  IClientSettings,
  IFlightEditorSettingsValue,
} from "@vmosolutions/oc-interfaces";

import { IRootState } from "src/redux/reducers/CombinedReducers";
import { bulkEvent } from "src/redux/actions/flights/FlightData.Actions";
import { getClientSetting } from "src/redux/actions/App.Actions";
import { LoadingMessage } from "../../LoadingMessage/LoadingMessage";

import { useDispatch, useSelector } from "react-redux";
import { DialogActions, Grid, Button, Typography, useTheme } from "@mui/material";
import { EventModalTypes } from "../DialogModalWrapper";
import { FlightChipCollection } from "src/components/Flights/FlightChip";
import { EventChipCollection } from "src/components/Flights/EventChip";
import { ModalTitle, ModalMessage, ModalButton, ModalErrorText } from "../DialogModalComponents";
import { isFlight } from "src/redux/reducers/FlightTimelineReducer";
import { resolveDefaultTaxiInMinutes } from "src/utils/airportHelper";

import { SelectField } from "../../FormikComponents/SelectField";
import { TextField } from "../../FormikComponents/TextField";

// Airport Retrieval
import { airportSearchString, updateAirportOptions, formatIdentFromAirport } from "src/utils/airportHelper"; //findAirports, filterAirports
import { IStructuresState, IAirportOption } from "src/redux/reducers/StructuresReducer"; //IAirportOptionMap
import { hrsMin2Milli, milli2DaysHrsMin } from "src/utils";
import { setSelectedEvents } from "src/redux/actions/flights/FlightTimeline.Actions";
import { getBlockDurationString } from "src/pages/FlightBuilder/Forms/FlightRowEdit";
import { DialogModalStyles } from "../DialogModalWrapper";
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";

/**
 * Check wether you can update the flights durations
 * must have the same flight number, origin, destination & no movement messages sent
 * @param flights-the flights to check
 * @returns false if you cannot set the duration
 */
const canSetDuration = (flights: IFlight[]): boolean => {
  const fltNumber = new Set();
  const origin = new Set();
  const destination = new Set();
  const movementMessages = new Set();
  flights.forEach((flt) => {
    fltNumber.add(flt.flightNumber);
    origin.add(flt.origin);
    destination.add(flt.destination);
    movementMessages.add(flt.receivedArrivalMessage);
    movementMessages.add(flt.receivedDepartureMessage);
  });
  return (
    fltNumber.size === 1 &&
    origin.size === 1 &&
    destination.size === 1 &&
    movementMessages.size === 1 &&
    movementMessages.has(false)
  );
};
/**
 * @param isOpen - to keep track of whither the modal should be open or closed
 * @param handleClose - sets isOpen to true or false in DialogModalWrapper.tsx
 * @param setActiveFlightModal - sets the  state of activeFlightModal in FlightTimelineWrapper.tsx
 * @returns the modal
 */

interface IDelayEventModalProps {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  setActiveEventModal: (EventModalTypes: EventModalTypes) => void;
}

interface IFieldOption {
  value: string;
  label: string;
}

interface IFlightPayload {
  // Flights
  flightNumber?: number;
  flightType?: IFlightType;
  origin?: string;
  destination?: string;
  departureGate?: string;
  arrivalGate?: string;
  // Ground Events
  title?: string;
  eventType?: IFlightType;
  location?: string;
  // Shared
  aircraft?: IFlightAircraft;
  customer?: ICustomer[];
  // Add/Remove flags from existing.
  flags?: IFlightFlagSubDoc[];
  addFlag?: IFlightFlagSubDoc;
  removeFlag?: IFlightFlagSubDoc;
  // Duration
  duration?: number;
  scheduledIn?: number;
  scheduledOn?: number;
  actualIn?: number;
  actualOn?: number;
}

const requiredFieldLabels: { [key: string]: string } = {
  flightNumber: "Flight #",
  flightType: "Flight Type",
  origin: "Origin",
  destination: "Destination",
  location: "Location",
};

export const BulkEditEventModal = (props: IDelayEventModalProps): JSX.Element => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { isOpen, handleClose, setActiveEventModal } = props;

  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { airportOptions, customerStructure } = structures;
  const { aircraft, flightTypes, customers, flightFlags, globalAppConfig } = customerStructure;
  const customerDefaultTaxiInMinutes = globalAppConfig?.customer?.settings.defaultTaxiInMinutes;
  const flightEventTypes = useMemo(() => {
    return (
      flightTypes
        ?.filter((type: IFlightType) => type.isFlight)
        .sort((a, b) => {
          return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
        }) ?? []
    );
  }, [flightTypes]);

  const groundEventTypes = useMemo(() => {
    return (
      flightTypes
        ?.filter((type: IFlightType) => !type.isFlight)
        .sort((a, b) => {
          return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
        }) ?? []
    );
  }, [flightTypes]);

  const selectedEvents: (IFlight | IGroundEvent)[] = useSelector(
    (state: IRootState) => state.timelineFlights.selectedEvents
  );

  // For BULK batch tracking
  const [batchId, setBatchId] = useState<any>(null);
  const batchJob: IBulkOpsState = useSelector((state: IRootState) => state.bulkBatch[batchId]);
  const [building, setBuilding] = useState<boolean>(false);
  const [buildStatus, setBuildStatus] = useState<string>(null);

  const [selectedFields, setSelectedFields] = useState<IFieldOption[]>([]);
  const [editPayload, setEditPayload] = useState<IFlightPayload>({});
  const [flightEditorSettings, setFlightEditorSettings] = useState<IFlightEditorSettingsValue>(null);
  const [departureGateCodes, setDepartureGateCodes] = useState<IFieldOption[]>([]);
  const [arrivalGateCodes, setArrivalGateCodes] = useState<IFieldOption[]>([]);
  const originValue: IAirportOption | null = React.useMemo(() => {
    if (!editPayload?.origin) return null;
    return formatIdentFromAirport(editPayload.origin, Object.values(airportOptions))[0];
  }, [airportOptions, editPayload]);

  const destValue: IAirportOption | null = React.useMemo(() => {
    if (!editPayload?.destination) return null;
    return formatIdentFromAirport(editPayload.destination, Object.values(airportOptions))[0];
  }, [airportOptions, editPayload]);

  const locationValue: IAirportOption | null = React.useMemo(() => {
    if (!editPayload?.location) return null;
    return formatIdentFromAirport(editPayload.location, Object.values(airportOptions))[0];
  }, [airportOptions, editPayload]);

  const [errorMessage, setErrorMessage] = useState<string>(null);

  const selectedFlights: IFlight[] = React.useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => isFlight(event));
  }, [selectedEvents]);

  const selectedGroundEvents: IGroundEvent[] = React.useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => !isFlight(event)) as IGroundEvent[];
  }, [selectedEvents]);

  // get type of event
  const typeFlight: boolean = selectedFlights.length > 0 ? true : false;

  const flightFieldOptions = useMemo(() => {
    setSelectedFields(null);
    return typeFlight
      ? [
          { value: "flightNumber", label: "Flight #", disabled: true }, // number
          { value: "flightType", label: "Flight Type", disabled: true }, // IBasicFlightType || IFlightType
          { value: "aircraft", label: "Aircraft", disabled: true }, // IFlightAircraft
          { value: "origin", label: "Origin", disable: true }, // icao string
          { value: "destination", label: "Destination", disable: true }, // icao string
          { value: "departureGate", label: "Departure Gate", disable: true }, // gate code string
          { value: "arrivalGate", label: "Arrival Gate", disable: true }, // gate code string
          { value: "customer", label: "Customer", disable: true }, // ICustomer[]
          { value: "addFlag", label: "Add Flag", disable: true }, // IFlightFlag
          { value: "removeFlag", label: "Remove Flag", disable: true }, // IFlightFlag
          { value: "duration", label: "Set New Duration", disable: true }, // IFlightFlag
        ]
      : [
          { value: "title", label: "Title" }, //string
          { value: "eventType", label: "Event Type" }, // IFlightType
          { value: "aircraft", label: "Aircraft" }, // IFlightAircraft
          { value: "location", label: "Location" }, // icao string
          { value: "customer", label: "Customer" }, // ICustomer[]
        ];
  }, [typeFlight]);

  useEffect(() => {
    if (batchJob) {
      //console.log(batchJob);
      const eventTypes = typeFlight ? "flights" : "ground events";
      const { records, successfulEventIds, failedEvents } = batchJob;
      const failedUpdates = failedEvents?.length > 0 ? `, ${failedEvents?.length} ${eventTypes} failed update` : "";
      setBuildStatus(`${successfulEventIds?.length} of ${records} ${eventTypes} successfully updated${failedUpdates}`);

      // Build completed
      if (successfulEventIds?.length + failedEvents?.length === records) {
        setBuilding(false);
      }
    }
  }, [batchJob, typeFlight]);

  // If user deselects a field, clear it from the payload.
  // If re-added with no value assigned, set it to null.
  useEffect(() => {
    setEditPayload((prev: { [key: string]: any }) => {
      return selectedFields
        ? selectedFields.reduce((acc, field: IFieldOption) => {
            const fieldValue = prev[field.value] ?? null;
            return { ...acc, [field.value]: fieldValue };
          }, {})
        : {};
    });
  }, [selectedFields]);

  useEffect(() => {
    const requiredFields = typeFlight ? ["flightNumber", "flightType", "origin", "destination"] : ["location"];

    const missingRequiredFields = Object.entries(editPayload).reduce<string[]>((acc, [field, value]) => {
      if (requiredFields.includes(field) && (value === null || value === "" || value === undefined)) {
        acc.push(requiredFieldLabels[field] ?? field);
      }
      return acc;
    }, []);

    if (missingRequiredFields.length === 0) {
      setErrorMessage(null);
      return;
    }

    const fieldsText = missingRequiredFields.join(", ");
    const errorString =
      missingRequiredFields.length === 1 ? `${fieldsText} is a required field` : `${fieldsText} are required fields`;
    setErrorMessage(errorString);
  }, [editPayload, typeFlight]);

  // Load flight editor settings (controls free-form vs. dropdown gate entry).
  useEffect(() => {
    const getFlightEditorSetting = async (): Promise<void> => {
      const flightEditorSetting: IClientSettings = await getClientSetting(ClientSettingName.FlightEditor);
      setFlightEditorSettings(flightEditorSetting?.setValue);
    };
    void getFlightEditorSetting();
  }, []);

  // Ensure the airport options (and their configured gates) are loaded for every
  // selected flight's origin & destination so the gate dropdowns can be populated.
  useEffect(() => {
    const idents = new Set<string>();
    selectedFlights.forEach((flight) => {
      if (flight.origin) idents.add(flight.origin);
      if (flight.destination) idents.add(flight.destination);
    });
    idents.forEach((ident) => void updateAirportOptions(ident, airportOptions, dispatch));
  }, [selectedFlights, airportOptions, dispatch]);

  // Build the gate code option lists as the union of configured gates across the
  // selected flights' origin (departure) and destination (arrival) airports.
  useEffect(() => {
    const departureCodes = new Set<string>();
    const arrivalCodes = new Set<string>();
    selectedFlights.forEach((flight) => {
      airportOptions[flight.origin]?.gates?.forEach((gate) => departureCodes.add(gate.code));
      airportOptions[flight.destination]?.gates?.forEach((gate) => arrivalCodes.add(gate.code));
    });
    setDepartureGateCodes(Array.from(departureCodes).map((code) => ({ label: code, value: code })));
    setArrivalGateCodes(Array.from(arrivalCodes).map((code) => ({ label: code, value: code })));
  }, [selectedFlights, airportOptions]);

  //check to make sure you aren't working with an empty array
  if (selectedEvents.length <= 0) {
    return null;
  }

  //////////////
  // HANDLERS //
  //////////////

  // Resets modal so that it can be used for a second edit as needed.
  const handleReset = (): void => {
    setBuildStatus(null);
    setSelectedFields([]);
    setEditPayload({});
  };

  const handleCancel = (): void => {
    // If a bulk operation was started and users choose to close the modal, clear all selected events making it easier for them to start a new bulk operation.
    if (buildStatus) dispatch(setSelectedEvents([]));
    handleClose(false);
    setActiveEventModal(EventModalTypes.None);
  };

  const handleConfirm = async (): Promise<void> => {
    //if there is no change do not update DB
    if (typeFlight) {
      setBuilding(true);
      setBuildStatus("Changing field values for selected flights");
      const selectedEventIds: string[] = [];
      const selectedFlightChanges = selectedFlights.reduce((acc: Partial<IFlight>[], flight: IFlight) => {
        selectedEventIds.push(flight._id);
        const flightPayload = { ...editPayload };
        // Gates clear with an empty string, never null (per bulk UPDATE_FLIGHT contract).
        if (Object.prototype.hasOwnProperty.call(flightPayload, "departureGate")) {
          flightPayload.departureGate = flightPayload.departureGate ?? "";
        }
        if (Object.prototype.hasOwnProperty.call(flightPayload, "arrivalGate")) {
          flightPayload.arrivalGate = flightPayload.arrivalGate ?? "";
        }
        // If desired flag addition is not found in selected flight, add it.
        if (flightPayload?.addFlag?._id) {
          flightPayload.flags = flight.flags.find((f) => f._id === editPayload.addFlag._id)
            ? [...flight.flags]
            : [...flight.flags, editPayload.addFlag];
        }
        // If desired flag removal is found in selected flight, remove it.
        if (flightPayload?.removeFlag?._id) {
          flightPayload.flags = [...flight.flags.filter((f) => f._id !== editPayload.removeFlag?._id)];
        }

        // If setting duration.
        if (flightPayload?.duration) {
          const destinationAirport = airportOptions?.[flight.destination];
          const taxiInMinutes = resolveDefaultTaxiInMinutes(destinationAirport, customerDefaultTaxiInMinutes);
          //by adding ms to epoch values I shouldn't have to worry about DST, right?
          flightPayload.scheduledIn = flight.scheduledOut?.valueOf() + flightPayload.duration?.valueOf();
          flightPayload.scheduledOn =
            flight.scheduledOut?.valueOf() +
            flightPayload.duration?.valueOf() -
            taxiInMinutes * minuteInMilliseconds;
          flightPayload.actualIn = flight.scheduledOut?.valueOf() + flightPayload.duration?.valueOf();
          flightPayload.actualOn =
            flight.scheduledOut?.valueOf() +
            flightPayload.duration?.valueOf() -
            taxiInMinutes * minuteInMilliseconds;
          delete flightPayload.duration;
        }
        acc.push({ _id: flight._id, ...flightPayload });
        return acc;
      }, []);
      const bulkPayload: IBulkEventPayload = {
        actionType: BulkActionType.UPDATE_FLIGHT,
        batchIds: selectedEventIds,
        payload: selectedFlightChanges,
      };
      const newBatchId = await dispatch(bulkEvent(bulkPayload, selectedFlights?.length));
      setBatchId(newBatchId);
    } else {
      setBuilding(true);
      setBuildStatus("Changing Start/End times for selected ground events");
      const selectedEventIds: string[] = [];
      const selectedEventChanges = selectedGroundEvents.reduce((acc: Partial<IGroundEvent>[], event: IGroundEvent) => {
        selectedEventIds.push(event._id);
        acc.push({ _id: event._id, ...editPayload });
        return acc;
      }, []);
      const bulkPayload: IBulkEventPayload = {
        actionType: BulkActionType.UPDATE_GROUND_EVENT,
        batchIds: selectedEventIds,
        payload: selectedEventChanges,
      };
      const newBatchId = await dispatch(bulkEvent(bulkPayload, selectedGroundEvents?.length));
      setBatchId(newBatchId);
    }
    //set the modal type to none on close
    //setActiveEventModal(EventModalTypes.None);
  };

  //////////////
  //  Return  //
  //////////////

  if (isOpen) {
    return (
      <DialogContent sx={DialogModalStyles(theme)}>
        <div className="modal_content">
          {typeFlight ? (
            <FlightChipCollection flights={selectedFlights} disableContext={true} disableFocus={true} />
          ) : (
            <EventChipCollection events={selectedGroundEvents} disableContext={true} disableFocus={true} />
          )}

          <ModalTitle title={"Bulk Edit Tool"}></ModalTitle>
          <ModalMessage
            message={`Update selected fields on ${typeFlight ? "flights" : "ground events"} listed above.`}
          ></ModalMessage>

          <Grid container className="bulk_fields" direction="column">
            <Grid item>
              <SelectField
                name="bulk_field_selector"
                className="bulk_field_selector"
                label="Fields"
                options={flightFieldOptions}
                onChange={setSelectedFields}
                value={selectedFields}
                menuPosition="fixed"
                isMulti={true}
                openMenuOnFocus={true}
                isClearable={true}
                placeholder="Select Fields to edit..."
                maxMenuHeight={400}
              />
            </Grid>

            <Grid container className="fields">
              {selectedFields?.find((option) => option.value === "flightNumber") && (
                <TextField
                  name="bulk_flightnumber_selector"
                  className="bulk_selector flightnumber"
                  label="Flight #"
                  onChange={(event: any) => {
                    const flightNumber = event?.target?.value ? Math.abs(Number(event?.target?.value)) : null;
                    setEditPayload((prev) => ({ ...prev, flightNumber }));
                  }}
                  // value={editPayload?.flightNumber?.toString()}
                />
              )}

              {selectedFields?.find((option) => option.value === "flightType") && (
                <SelectField
                  name="bulk_flighttype_selector"
                  className="bulk_selector flighttype"
                  label={"Flight Type"}
                  options={flightEventTypes}
                  getOptionLabel={(flightType: IFlightType) => `${flightType.description} (${flightType.title})`}
                  getOptionValue={(flightType: IFlightType) => flightType._id}
                  onChange={(flightType) => {
                    setEditPayload((prev) => ({ ...prev, flightType }));
                  }}
                  value={editPayload?.flightType}
                  menuPosition="fixed"
                  isSearchable={true}
                  openMenuOnFocus={false}
                  isClearable={true}
                  maxMenuHeight={200}
                />
              )}

              {selectedFields?.find((option) => option.value === "title") && (
                <TextField
                  name="bulk_title_selector"
                  className="bulk_selector"
                  label="Title"
                  onChange={(event: any) => {
                    const title = event?.target?.value ?? null;
                    setEditPayload((prev) => ({ ...prev, title }));
                  }}
                />
              )}

              {selectedFields?.find((option) => option.value === "eventType") && (
                <SelectField
                  name="bulk_eventtype_selector"
                  className="bulk_selector eventtype"
                  label={"Event Type"}
                  options={groundEventTypes}
                  getOptionLabel={(eventType: IFlightType) => `${eventType.description} (${eventType.title})`}
                  getOptionValue={(eventType: IFlightType) => eventType._id}
                  onChange={(eventType) => {
                    setEditPayload((prev) => ({ ...prev, eventType }));
                  }}
                  value={editPayload?.eventType}
                  menuPosition="fixed"
                  isSearchable={true}
                  openMenuOnFocus={false}
                  isClearable={true}
                  maxMenuHeight={200}
                />
              )}

              {selectedFields?.find((option) => option.value === "aircraft") && (
                <SelectField
                  name="bulk_aircraft_selector"
                  className="bulk_selector aircraft"
                  label="Aircraft"
                  options={aircraft}
                  getOptionLabel={(aircraft: IFlightAircraft) =>
                    `${aircraft.registration} (${aircraft.aircraftType && aircraft.aircraftType.title})`
                  }
                  getOptionValue={(aircraft: IFlightAircraft) => aircraft._id}
                  onChange={(aircraft) => {
                    setEditPayload((prev) => ({ ...prev, aircraft }));
                  }}
                  value={editPayload?.aircraft}
                  menuPosition="fixed"
                  isSearchable={true}
                  openMenuOnFocus={false}
                  isClearable={true}
                  maxMenuHeight={200}
                />
              )}

              {selectedFields?.find((option) => option.value === "origin") && (
                <SelectField
                  name="bulk_origin_selector"
                  className="bulk_selector origin"
                  label="Origin"
                  onInputChange={(ident: string) => {
                    updateAirportOptions(ident, airportOptions, dispatch);
                  }}
                  options={Object.values(airportOptions)}
                  getOptionLabel={(airport: IAirportOption) => `${airport.ident} - ${airport.name}`}
                  getOptionValue={(airport: IAirportOption) => airport.ident}
                  onChange={(airport: IAirportOption) => {
                    setEditPayload((prev) => ({ ...prev, origin: airport.ident }));
                  }}
                  value={originValue}
                  isSearchable={true}
                  menuPosition={"fixed"}
                  openMenuOnFocus={false}
                  isClearable={false}
                  maxMenuHeight={200}
                  noOptionsMessage={() => airportSearchString}
                />
              )}

              {selectedFields?.find((option) => option.value === "destination") && (
                <SelectField
                  name="bulk_destination_selector"
                  className="bulk_selector destination"
                  label="Destination"
                  onInputChange={(ident: string) => {
                    updateAirportOptions(ident, airportOptions, dispatch);
                  }}
                  options={Object.values(airportOptions)}
                  getOptionLabel={(airport: IAirportOption) => `${airport.ident} - ${airport.name}`}
                  getOptionValue={(airport: IAirportOption) => airport.ident}
                  onChange={(airport: IAirportOption) => {
                    setEditPayload((prev) => ({ ...prev, destination: airport.ident }));
                  }}
                  value={destValue}
                  isSearchable={true}
                  menuPosition={"fixed"}
                  openMenuOnFocus={false}
                  isClearable={false}
                  maxMenuHeight={200}
                  noOptionsMessage={() => airportSearchString}
                />
              )}

              {selectedFields?.find((option) => option.value === "location") && (
                <SelectField
                  name="bulk_location_selector"
                  className="bulk_selector location"
                  label="Location"
                  onInputChange={(ident: string) => {
                    updateAirportOptions(ident, airportOptions, dispatch);
                  }}
                  options={Object.values(airportOptions)}
                  getOptionLabel={(airport: IAirportOption) => `${airport.ident} - ${airport.name}`}
                  getOptionValue={(airport: IAirportOption) => airport.ident}
                  onChange={(airport: IAirportOption) => {
                    setEditPayload((prev) => ({ ...prev, location: airport.ident }));
                  }}
                  value={locationValue}
                  isSearchable={true}
                  menuPosition={"fixed"}
                  openMenuOnFocus={false}
                  isClearable={false}
                  maxMenuHeight={200}
                  noOptionsMessage={() => airportSearchString}
                />
              )}

              {selectedFields?.find((option) => option.value === "departureGate") &&
                (flightEditorSettings?.gateFreeFormTextInputEnabled || departureGateCodes.length === 0 ? (
                  <TextField
                    name="bulk_departuregate_selector"
                    className="bulk_selector gate"
                    label="Departure Gate"
                    value={editPayload?.departureGate ?? ""}
                    onChange={(event: any) => {
                      const departureGate = event?.target?.value ?? "";
                      setEditPayload((prev) => ({ ...prev, departureGate }));
                    }}
                  />
                ) : (
                  <SelectField
                    name="bulk_departuregate_selector"
                    className="bulk_selector gate"
                    label="Departure Gate"
                    options={departureGateCodes}
                    onChange={(gate: IFieldOption) => {
                      setEditPayload((prev) => ({ ...prev, departureGate: gate?.value ?? "" }));
                    }}
                    value={
                      editPayload?.departureGate
                        ? { label: editPayload.departureGate, value: editPayload.departureGate }
                        : null
                    }
                    placeholder="Select Gate"
                    menuPosition="fixed"
                    isSearchable={true}
                    openMenuOnFocus={false}
                    isClearable={true}
                    maxMenuHeight={200}
                  />
                ))}

              {selectedFields?.find((option) => option.value === "arrivalGate") &&
                (flightEditorSettings?.gateFreeFormTextInputEnabled || arrivalGateCodes.length === 0 ? (
                  <TextField
                    name="bulk_arrivalgate_selector"
                    className="bulk_selector gate"
                    label="Arrival Gate"
                    value={editPayload?.arrivalGate ?? ""}
                    onChange={(event: any) => {
                      const arrivalGate = event?.target?.value ?? "";
                      setEditPayload((prev) => ({ ...prev, arrivalGate }));
                    }}
                  />
                ) : (
                  <SelectField
                    name="bulk_arrivalgate_selector"
                    className="bulk_selector gate"
                    label="Arrival Gate"
                    options={arrivalGateCodes}
                    onChange={(gate: IFieldOption) => {
                      setEditPayload((prev) => ({ ...prev, arrivalGate: gate?.value ?? "" }));
                    }}
                    value={
                      editPayload?.arrivalGate
                        ? { label: editPayload.arrivalGate, value: editPayload.arrivalGate }
                        : null
                    }
                    placeholder="Select Gate"
                    menuPosition="fixed"
                    isSearchable={true}
                    openMenuOnFocus={false}
                    isClearable={true}
                    maxMenuHeight={200}
                  />
                ))}

              {selectedFields?.find((option) => option.value === "customer") && (
                <SelectField
                  name="bulk_customer_selector"
                  className="bulk_selector customer"
                  label="Customer"
                  options={customers}
                  getOptionLabel={(customer: ICustomer) => customer.name}
                  getOptionValue={(customer: ICustomer) => customer._id}
                  onChange={(customers: ICustomer[]) => {
                    setEditPayload((prev) => ({ ...prev, customer: customers }));
                  }}
                  isMulti={true}
                  value={editPayload?.customer}
                  menuPosition="fixed"
                  isSearchable={true}
                  openMenuOnFocus={false}
                  isClearable={true}
                />
              )}

              {selectedFields?.find((option) => option.value === "addFlag") && (
                <SelectField
                  name="bulk_flag_selector"
                  className="bulk_selector flag"
                  label="Add Flag"
                  options={flightFlags}
                  getOptionLabel={(flag: IFlightFlag) => `${flag.description} [ ${flag.title} ]`}
                  getOptionValue={(flag: IFlightFlag) => flag._id}
                  onChange={(flag: IFlightFlag) => {
                    setEditPayload((prev) => ({ ...prev, addFlag: flag }));
                  }}
                  menuPosition="fixed"
                  value={editPayload?.addFlag}
                  // isSearchable={true}

                  openMenuOnFocus={false}
                  isClearable={true}
                />
              )}

              {selectedFields?.find((option) => option.value === "removeFlag") && (
                <SelectField
                  name="bulk_flag_selector"
                  className="bulk_selector flag"
                  label="Remove Flag"
                  options={flightFlags}
                  getOptionLabel={(flag: IFlightFlag) => `${flag.description} [ ${flag.title} ]`}
                  getOptionValue={(flag: IFlightFlag) => flag._id}
                  onChange={(flag: IFlightFlag) => {
                    setEditPayload((prev) => ({ ...prev, removeFlag: flag }));
                  }}
                  menuPosition="fixed"
                  value={editPayload?.removeFlag}
                  // isSearchable={true}

                  openMenuOnFocus={false}
                  isClearable={true}
                />
              )}

              {selectedFields?.find((option) => option.value === "duration") && (
                <Grid container alignItems="center">
                  <DateTimeField
                    name={"duration_selector"}
                    className="bulk_selector datetimeinput" // bulk_selector
                    label="Block Time"
                    disabled={!canSetDuration(selectedFlights)}
                    value={
                      editPayload?.duration
                        ? milli2DaysHrsMin(editPayload?.duration).padStart(5, "0")
                        : getBlockDurationString(
                            selectedFlights?.[0].scheduledOut?.valueOf(),
                            selectedFlights?.[0].scheduledIn?.valueOf()
                          )
                    }
                    onChange={(event: any) => {
                      //convert HH:MM to milseconds

                      const duration = hrsMin2Milli(event);
                      setEditPayload((prev) => ({ ...prev, duration }));
                    }}
                    isEpoch={false}
                    displayOnlyTime={true}
                  />
                  {!canSetDuration(selectedFlights) && (
                    <Typography variant="caption" color="error">
                      Block Duration cannot be modified for these flights.
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>

            {!!errorMessage && <ModalErrorText error={errorMessage}></ModalErrorText>}
          </Grid>

          <DialogActions>
            <Button
              className="reset_btn btn"
              type="reset"
              variant="text"
              color="secondary"
              disabled={!editPayload || building}
              onClick={handleReset}
            >
              Reset
            </Button>
            <ModalButton label={buildStatus ? "Close" : "Cancel"} handler={handleCancel} />
            <ModalButton
              label={`Update ${typeFlight ? "flights" : "ground events"}`}
              disable={!editPayload || building || !!buildStatus || !!errorMessage}
              handler={handleConfirm}
            />
          </DialogActions>

          <div className="modal_status">
            {!!buildStatus && <LoadingMessage message={buildStatus} animate={building} />}
          </div>
        </div>
      </DialogContent>
    );
  } else {
    return null;
  }
};
